import {
  canHoldConfiguration,
  canHoldPublicHolidays,
  canHoldEmployment,
  ORG_UNIT_SETTINGS_SECTIONS,
} from '../../../shared';
import type { OrgUnitSettingsSectionId } from '../../../shared';
import type { OrganizationUnitType } from '../../../shared';

/**
 * How the organization unit's configuration is grouped.
 *
 * The panel shows two tabs, and each tab lists its sections before showing any
 * of them. The grouping follows the subject matter rather than the permissions,
 * although the two nearly coincide today: cost centres hang off
 * `COST_CENTER_ADMIN`, booking types / time types / accounts off
 * `TIME_MANAGEMENT_ADMIN`, and the rule set off its own `DSL_ADMIN`.
 *
 * The host keeps passing plain section ids, unchanged. Which tab a section
 * belongs to is decided here, so adding one is a single entry in this file.
 */

/*
  Declared in the module's shared layer, not here: the server-route sweep has
  to read the same list, and it cannot import anything Angular. Re-exported so
  every existing importer keeps its path.
*/
export {
  ORG_UNIT_SETTINGS_SECTIONS,
  type OrgUnitSettingsSectionId,
} from '../../../shared';

/**
 * The unit's own record, offered above the configuration groups.
 *
 * Not a member of `ORG_UNIT_SETTINGS_GROUPS`: a group drills down to a list of
 * sections, and this one goes straight to a form. It is kept in the same union
 * so the panel has one notion of "where am I", rather than a second flag that
 * could disagree with the first.
 */
export const UNIT_GROUP_ID = 'unit';

export type OrgUnitSettingsGroupId =
  | 'employment'
  | 'costAccounting'
  | 'timeManagement';

/** Where the top-level list can lead: the unit itself, or a group. */
export type OrgUnitSettingsTopLevelId =
  | typeof UNIT_GROUP_ID
  | OrgUnitSettingsGroupId;

export interface OrgUnitSettingsGroup {
  readonly id: OrgUnitSettingsGroupId;
  readonly sections: readonly OrgUnitSettingsSectionId[];
  /**
   * Which units may show the group at all, beyond holding configuration.
   *
   * Configuration is kept on the root as well, because everything below
   * inherits it. A contract is inherited by nobody — it belongs to the legal
   * entity that employs the person, and the root is not one. Without this the
   * group would appear on the root and offer to create something the server
   * refuses.
   */
  readonly holds?: (type: OrganizationUnitType) => boolean;
}

/** Order here is the order on screen, in both the tab strip and the lists. */
export const ORG_UNIT_SETTINGS_GROUPS: readonly OrgUnitSettingsGroup[] = [
  // First: who works here comes before the rules their time is measured by.
  { id: 'employment', sections: ['employments'], holds: canHoldEmployment },
  { id: 'costAccounting', sections: ['costCenters'] },
  {
    id: 'timeManagement',
    // Day programs sit after time types: a day program's intervals reference
    // them, so that is the order someone sets these up in.
    sections: [
      'bookingTypes',
      'timeTypes',
      'dayPrograms',
      'workSchedules',
      // After the Zeitmodelle: a holiday takes its effect off the day those
      // plan, so this is the order somebody sets them up in.
      'publicHolidays',
      'accounts',
      'rules',
    ],
  },
];

function isSectionId(value: string): value is OrgUnitSettingsSectionId {
  return (ORG_UNIT_SETTINGS_SECTIONS as readonly string[]).includes(value);
}

/**
 * The groups worth showing, each narrowed to the sections the actor may see.
 *
 * A group whose every section is out of reach is dropped rather than rendered
 * empty: a tab that opens onto nothing reads as a fault, not as a permission.
 *
 * A department has none at all, whatever the actor may do: configuration is
 * maintained on the root and on companies and inherited from there. Its own
 * record stays — that is a separate entry, not a group.
 */
/**
 * Sections that only some unit types keep, beyond what the group says.
 *
 * The group's own `holds` is too coarse for this: `publicHolidays` sits in
 * the time-management group beside six sections every configuring unit has,
 * and splitting the group to hold one predicate would reorder the tab for
 * everybody.
 */
const SECTION_HOLDS: Partial<
  Record<OrgUnitSettingsSectionId, (type: OrganizationUnitType) => boolean>
> = {
  publicHolidays: canHoldPublicHolidays,
};

/**
 * Sections whose own view wants the screen, before any form is open.
 *
 * `wide` on the open entry covers the other case — a form that needs more
 * room than the sheet's 680px, which is how the rule editor asks. The
 * holiday calendar needs it for the *list*: a twelve-month grid at 680px is
 * three months wide and nine months of scrolling.
 */
const WIDE_SECTIONS: ReadonlySet<OrgUnitSettingsSectionId> = new Set([
  'publicHolidays',
]);

export function wantsWideSheet(
  section: OrgUnitSettingsSectionId | null,
): boolean {
  return section !== null && WIDE_SECTIONS.has(section);
}

export function visibleGroups(
  available: readonly string[],
  unitType: OrganizationUnitType,
): readonly OrgUnitSettingsGroup[] {
  if (!canHoldConfiguration(unitType)) return [];

  const allowed = new Set(available.filter(isSectionId));

  return ORG_UNIT_SETTINGS_GROUPS.filter(
    (group) => group.holds?.(unitType) ?? true,
  )
    .map((group) => ({
      id: group.id,
      sections: group.sections.filter(
        (section) =>
          allowed.has(section) && (SECTION_HOLDS[section]?.(unitType) ?? true),
      ),
    }))
    .filter((group) => group.sections.length > 0);
}

/** Which group a section sits in, or `null` for an id nothing declares. */
export function groupOfSection(
  section: string,
): OrgUnitSettingsGroupId | null {
  return (
    ORG_UNIT_SETTINGS_GROUPS.find((group) =>
      (group.sections as readonly string[]).includes(section),
    )?.id ?? null
  );
}

/** Where the panel opens: `null` on both counts is the top-level group list. */
export interface OrgUnitSettingsEntryPoint {
  readonly group: OrgUnitSettingsTopLevelId | null;
  readonly section: OrgUnitSettingsSectionId | null;
}

/**
 * Where to open the panel.
 *
 * `remembered` is the section last looked at, kept across closing the panel so
 * working through several units in a row does not mean re-navigating each time.
 * It is honoured only while it is still permitted and still exists.
 *
 * Otherwise the panel opens on the group list — the top of the drill-down, and
 * the same place for everyone, rather than guessing at a group. Even with a
 * single visible group: skipping a one-entry list would make the panel behave
 * differently depending on the actor's permissions.
 */
export function resolveEntryPoint(
  available: readonly string[],
  remembered: string | null,
  unitType: OrganizationUnitType,
): OrgUnitSettingsEntryPoint | null {
  const groups = visibleGroups(available, unitType);
  if (groups.length === 0) return null;

  if (remembered && isSectionId(remembered)) {
    const group = groups.find((candidate) =>
      candidate.sections.includes(remembered),
    );
    if (group) return { group: group.id, section: remembered };
  }

  return { group: null, section: null };
}
